/* ═══════════════════════════════════════════════════════════════
   Master Forma Studio — right-click menu for a sample body paragraph.

   What you right-click decides the menu: a level row offers that level's
   style panel and a jump to each geometry group in it (Indents, Pagination,
   Numbering). Every item drives something the Levels panel already does —
   "Level style…" is the left-click, each jump opens the panel and focuses
   that group (LevelProps' own `data-level-group` markers), "Reset to CSI
   default" is the panel's reset. Character, font, alignment, spacing and
   "Same as Typography" moved to the Format Bar (SYNC_PLAN §3.0).
   No new data, no new permission: edits ride the same `edit` the panel
   uses; without it the two writing items stay visible but disabled.

   Built on the kit ContextMenu (DESIGN §4 / §5) — no hand-rolled menu.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { StandardConfig } from '../../shared/types';
import { isCsiDefault, resetLevelToCsi } from '../../shared/csi-defaults';
import { CSI_NUMBER_FORMATS } from '../constants';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '../components/ui/context-menu';
import { focusLevelGroup, type LevelGroupId } from '../components/LevelProps';
import type { FormaEdit } from './panels/FormaPalettes';
import { rem } from '../lib/rem';

const READ_ONLY_REASON = 'View only — you need permission to edit formas to change this.';

const GROUPS: Array<{ id: LevelGroupId; label: string }> = [
  { id: 'indents', label: 'Indents…' },
  { id: 'pagination', label: 'Pagination…' },
  { id: 'numbering', label: 'Numbering…' },
];

export interface LevelContextMenuProps {
  at: { x: number; y: number };
  level: number;
  forma: StandardConfig | null;
  /** Absent → read-only: the two writing items are visible but disabled. */
  edit?: FormaEdit;
  /** Same as a left-click on the row: point the Levels panel at this level. */
  onLevelStyle: (level: number) => void;
  onClose: () => void;
}

export function LevelContextMenu({
  at,
  level,
  forma,
  edit,
  onLevelStyle,
  onClose,
}: LevelContextMenuProps) {
  const idx = Math.max(0, Math.min(8, level - 1));
  const target = forma?.levels?.[idx];
  const fmtInfo = (CSI_NUMBER_FORMATS as any)[idx];
  const hasNumbering = !!fmtInfo && fmtInfo.options.length > 1;
  const canReset = !!target && !isCsiDefault(target);
  const lockTitle = edit ? undefined : READ_ONLY_REASON;

  const jump = (group: LevelGroupId) => {
    onLevelStyle(level);
    focusLevelGroup(group);
  };
  const writeLevel = (fn: (l: any) => any) =>
    edit?.((f) => {
      const levels = [...f.levels];
      levels[idx] = fn(levels[idx]);
      return { ...f, levels };
    });

  return (
    <ContextMenu open modal={false} onOpenChange={(next) => !next && onClose()}>
      {createPortal(
        <ContextMenuTrigger asChild>
          <span
            aria-hidden="true"
            style={{
              position: 'fixed',
              left: at.x,
              top: at.y,
              width: rem(1),
              height: rem(1),
              pointerEvents: 'none',
            }}
          />
        </ContextMenuTrigger>,
        document.body
      )}
      <ContextMenuContent
        className="dsg-menu min-w-[13rem]"
        align="start"
        sideOffset={0}
        collisionPadding={8}
        aria-label={`Level ${level} actions`}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <ContextMenuItem className="dsg-menu-item" onSelect={() => onLevelStyle(level)}>
          Level style…
        </ContextMenuItem>
        <ContextMenuSeparator className="dsg-menu-sep" />
        {GROUPS.filter((g) => g.id !== 'numbering' || hasNumbering).map((g) => (
          <ContextMenuItem key={g.id} className="dsg-menu-item" onSelect={() => jump(g.id)}>
            {g.label}
          </ContextMenuItem>
        ))}
        {target && (
          <>
            <ContextMenuSeparator className="dsg-menu-sep" />
            {canReset && (
              <ContextMenuItem
                className="dsg-menu-item"
                disabled={!edit}
                title={
                  lockTitle ??
                  'Put this level’s spacing and indents back to the published CSI SectionFormat convention'
                }
                onSelect={() => writeLevel((l) => resetLevelToCsi(l))}
              >
                Reset to CSI default
              </ContextMenuItem>
            )}
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
